import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { CheckCircle2, Lightbulb, ChevronDown, ChevronRight, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import WeightManagementAboutSheet from "../WeightManagementAboutSheet";
import {
  measureCardHeading,
  gluceraHomeMeta,
  gluceraHomeStatusBadge,
  gluceraHomeEyebrow,
} from "../../utils/gluceraTypography";
import {
  homeAccentIconShell,
  homeAccentLink,
  homeAccentText,
  homeCardTopWash,
  homeLabelBadgeClass,
  homeProgressBarClass,
  homeTipsBody,
  homeTipsLabel,
  homeTipsShell,
} from "../../utils/homeBlueTheme";
import { cn } from "../../lib/utils";

interface WeightManagementApiData {
  wms_score: number;
  predicted_kg_4wk: number;
  predicted_change_display?: string;
  ci80_lo: number;
  ci80_hi: number;
  prob_loss_half_kg: number;
  prob_loss_1_kg: number;
  levers: {
    energy_balance: number;
    meal_timing: number;
    protein: number;
    sleep: number;
    meal_quality: number;
    gut_health: number;
  };
  primary_lever: string;
  primary_lever_key: string;
  prescription_text: string;
  score_band: string;
  score_message: string;
  prediction_tone: string;
  lever_tips: Record<string, string>;
  body_composition_score: number;
  tdee_kcal: number;
  energy_balance_kcal: number;
  meta: {
    meals_in_window: number;
    gis_from_home_snapshots: boolean;
  };
}

interface WeightManagementCardProps {
  data?: WeightManagementApiData;
  disableNavigation?: boolean;
  /** Home: compact summary row — tap opens weight detail page */
  summaryOnly?: boolean;
}

const LEVER_LABELS: Record<string, string> = {
  // For levers object (with underscores)
  energy_balance: "Energy Balance",
  meal_timing: "Meal Timing",
  protein: "Protein",
  sleep: "Sleep",
  meal_quality: "Meal Quality",
  gut_health: "Gut Health",
  // For lever_tips object (with spaces)
  "energy balance": "Energy Balance",
  "meal timing": "Meal Timing",
  "protein quality": "Protein Quality",
  "meal glycaemics": "Meal Quality (Glycaemics)",
  "gut health": "Gut Health",
  "adipose quality": "Fat Cell Quality",
};

const WeightManagementCard: React.FC<WeightManagementCardProps> = ({
  data,
  disableNavigation = false,
  summaryOnly = false,
}) => {
  const navigate = useNavigate();
  const [showAllTips, setShowAllTips] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  if (!data) return null;

  // Factors from levers
  const factors = Object.entries(data.levers).map(([key, value]) => ({
    label: LEVER_LABELS[key] || key,
    value: Math.round(value),
    key: key,
  }));

  // Get all tips directly from API (show all 7 tips)
  const allTips = Object.entries(data.lever_tips || {}).map(([key, tip]) => ({
    label: LEVER_LABELS[key] || key.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    tip: tip,
    key: key,
  }));

  const tipsToShow = showAllTips ? allTips : allTips.slice(0, 3);

  const getBarColor = (value: number) => homeProgressBarClass(value);

  // Human-friendly label shown instead of the raw number on the Home summary.
  const BAND_LABELS: Record<string, string> = {
    excellent: "Excellent",
    good: "Good",
    moderate: "Needs improvement",
    poor: "Poor",
  };
  const bandLabel =
    BAND_LABELS[data.score_band] ??
    (data.score_band
      ? data.score_band.charAt(0).toUpperCase() + data.score_band.slice(1)
      : "—");

  const bandBadgeClass = homeLabelBadgeClass(bandLabel, data.wms_score);

  const goToDetail = () =>
    navigate(`/glucera${GluceraNavigationRoutes.WEIGHT_MANAGEMENT_DETAIL}`);

  const openAbout = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setAboutOpen(true);
  };

  const infoButton = (
    <button
      type="button"
      onClick={openAbout}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border/80 bg-card text-muted-foreground shadow-sm transition-colors hover:bg-muted/60 hover:text-foreground"
      aria-label="About this score"
    >
      <Info className="h-4 w-4" strokeWidth={2.25} />
    </button>
  );

  if (summaryOnly) {
    return (
      <>
        <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
          <div
            className={cn("pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b", homeCardTopWash)}
            aria-hidden
          />
          <div className="absolute right-3 top-3 z-10">{infoButton}</div>
          <button
            type="button"
            className="relative flex w-full items-center gap-3 p-4 pr-12 text-left outline-none ring-offset-background transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring"
            onClick={goToDetail}
          >
          <div className="flex min-w-0 flex-1 flex-col items-start">
            <span className={measureCardHeading}>Weight Management</span>
            {data.score_band ? (
              <span
                className={cn(
                  "mt-2 inline-flex rounded-full px-2.5 py-1",
                  gluceraHomeStatusBadge,
                  bandBadgeClass
                )}
              >
                {bandLabel}
              </span>
            ) : null}
          </div>
          <ChevronRight
            className="h-5 w-5 shrink-0 text-muted-foreground"
            strokeWidth={2}
            aria-hidden
          />
        </button>
        </div>
        <WeightManagementAboutSheet open={aboutOpen} onClose={() => setAboutOpen(false)} />
      </>
    );
  }

  return (
    <>
    <Card
      className={cn(
        "w-full overflow-hidden rounded-2xl border-2 border-border bg-card shadow-lg transition-all duration-300",
        !disableNavigation
          ? "cursor-pointer hover:scale-[1.01] hover:shadow-xl active:scale-[0.99]"
          : ""
      )}
      onClick={!disableNavigation ? goToDetail : undefined}
    >
      <CardContent className="flex flex-col p-5 xs:p-6">
        <div className="pointer-events-none mb-5 flex w-full items-center justify-between gap-2">
          <h3 className="text-lg font-bold text-foreground xs:text-xl">
            Weight Management
          </h3>
          <div className="pointer-events-auto flex items-center gap-2">
            {infoButton}
            {data.score_band ? (
              <span
                className={cn(
                  "inline-flex items-center rounded-lg px-3 py-1.5 shadow-sm",
                  gluceraHomeStatusBadge,
                  bandBadgeClass
                )}
              >
                {data.score_band}
              </span>
            ) : null}
          </div>
        </div>

        <>
        <div className="border-t border-border/60 pt-3">
          <p className={cn("mb-3", gluceraHomeEyebrow)}>Key factors</p>
          <div className="space-y-3">
            {factors.map((factor) => {
              const pct = Math.min((factor.value / 100) * 100, 100);
              return (
                <div key={factor.label} className="flex items-center gap-3">
                  <span className="w-28 flex-shrink-0 text-xs xs:text-sm font-medium text-foreground">
                    {factor.label}
                  </span>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden shadow-inner">
                    <div
                      className={`h-full rounded-full transition-all duration-500 shadow-sm ${getBarColor(factor.value)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-sm font-bold text-foreground">
                    {factor.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tips Section */}
        {allTips.length > 0 && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn("flex h-7 w-7 items-center justify-center rounded-lg", homeAccentIconShell)}>
                  <Lightbulb className={cn("h-4 w-4", homeAccentText)} />
                </div>
                <p className="text-xs xs:text-sm font-bold uppercase tracking-wider text-foreground">
                  Tips to Improve
                </p>
              </div>
              {allTips.length > 3 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllTips(!showAllTips);
                  }}
                  className={cn("flex items-center gap-1 text-xs font-semibold hover:underline", homeAccentLink)}
                >
                  {showAllTips ? 'Show Less' : `+${allTips.length - 3} More`}
                  <ChevronDown className={`h-3 w-3 transition-transform ${showAllTips ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
            <div className="space-y-3">
              {tipsToShow.map((item) => (
                <div
                  key={item.key}
                  className={homeTipsShell + " p-3.5"}
                >
                  <div className="mb-2">
                    <span className={cn("text-xs font-bold uppercase tracking-wide", homeTipsLabel)}>
                      {item.label}
                    </span>
                  </div>
                  <p className={cn("text-xs xs:text-sm leading-relaxed", homeTipsBody)}>
                    {item.tip}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prescription Text */}
        {data.prescription_text && (
          <div className={cn("mt-5 rounded-xl border p-4", homeTipsShell)}>
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 flex-shrink-0">
                <div className={cn("flex h-6 w-6 items-center justify-center rounded-lg", homeAccentIconShell)}>
                  <CheckCircle2 className={cn("h-4 w-4", homeAccentText)} />
                </div>
              </div>
              <p className={cn("text-sm font-medium leading-relaxed xs:text-base", homeTipsBody)}>
                {data.prescription_text}
              </p>
            </div>
          </div>
        )}

        </>
      </CardContent>
    </Card>
    <WeightManagementAboutSheet open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
};

export default WeightManagementCard;
